/**
 * FloatingQuoteButton - Persistent "Get Free Quote" floating button
 * Appears on scroll, bottom-right, desktop-only (mobile uses StickyCTABar)
 */
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageSquareText } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContextNew';
import { startHref } from '@/lib/ctaSource';

const labels: Record<string, string> = {
  en: 'Request a Quote',
  fr: 'Demander un devis',
  es: 'Solicitar presupuesto',
  ar: 'اطلب عرض سعر',
  de: 'Angebot anfordern',
  it: 'Richiedi preventivo',
  pt: 'Solicitar orçamento',
  nl: 'Offerte aanvragen',
  pl: 'Zapytaj o wycenę',
  ru: 'Запросить расчёт',
  zh: '获取报价',
  ja: 'お見積もり',
  tr: 'Teklif isteyin',
  ko: '견적 요청',
  sv: 'Begär offert',
  da: 'Anmod om tilbud',
  ro: 'Solicitați o ofertă',
  hu: 'Ajánlatkérés',
  fi: 'Pyydä tarjous',
  hi: 'कोटेशन अनुरोध',
};

export function FloatingQuoteButton() {
  const [visible, setVisible] = useState(false);
  const { language } = useLanguage();
  const location = useLocation();

  // Hide on admin/portal/auth/checkout pages
  const hiddenPaths = ['/admin', '/portal', '/auth', '/checkout', '/agent'];
  // Also hide on lot 3C neutralized BPO pages (no "free quote" claim there)
  const lot3CSlugs = ['/live-chat-messaging', '/inbound-customer-support', '/back-office-data-processing'];
  const shouldHide = hiddenPaths.some(p => location.pathname.includes(p))
    || lot3CSlugs.some(s => location.pathname.includes(s));

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 400);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  if (shouldHide) return null;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8, x: 20 }}
          animate={{ opacity: 1, scale: 1, x: 0 }}
          exit={{ opacity: 0, scale: 0.8, x: 20 }}
          className="fixed bottom-24 right-6 z-40 hidden md:block"
        >
          <Link
            to={startHref(language, location.pathname, 'floating-quote')}
            className="flex items-center gap-2 px-5 py-3 rounded-full bg-gradient-to-r from-orange to-orange-light text-white font-semibold shadow-lg shadow-orange/30 hover:shadow-xl hover:shadow-orange/40 transition-shadow"
          >
            <MessageSquareText className="h-5 w-5" aria-hidden />
            {labels[language] || labels.en}
          </Link>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
